import type { WorkspaceRoleKey } from 'hooks/internalApi';
import clsx from 'clsx';
import { useToast } from 'components/ToastManager';
import { useUpdateApiClientMutation } from 'hooks/internalApi';
import { useHasWorkspacePermission } from 'hooks/useHasWorkspacePermission';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import { useWorkspaceRoleOptions } from 'hooks/useWorkspaceRoleOptions';
import React, { type FC, useCallback, useId, useState } from 'react';
import { getResultOrError } from 'utils/errors';
import { workspaceRoleLabels } from 'utils/workspaceRoles';
import { useWorkspace, useWorkspaceId } from 'wrappers/WorkspaceProvider';

import { Permissions } from '@fragment/authorization-utils/permissions';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { CodeBlock } from '@fragment/ui/src/components/CodeBlock/CodeBlock';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { Tabs } from '@fragment/ui/src/components/Tabs/Tabs';
import { TextInputCopy } from '@fragment/ui/src/components/TextInputCopy/TextInputCopy';
import { Toast } from '@fragment/ui/src/components/Toast';

import type { APIClientNode } from '../types';
import { apiClientFieldLabelClassName } from '../apiClientFieldStyles';
import { DeleteClientForm } from './DeleteClientForm';

type DetailTab = 'string' | 'json' | 'cli';

const detailTabOptions = [
  { label: 'String', value: 'string' as const },
  { label: 'JSON', value: 'json' as const },
  { label: 'CLI', value: 'cli' as const },
];

type ClientDetailProps = {
  apiClient: APIClientNode;
  initialTab?: DetailTab;
  onRoleUpdated?: (client: APIClientNode) => void;
} & (
  | { clientSecret: string; onClose: () => void; onConfirmedDelete?: never }
  | { clientSecret?: undefined; onClose?: never; onConfirmedDelete: () => void }
);

export const ClientDetail: FC<ClientDetailProps> = ({
  apiClient,
  clientSecret,
  initialTab = 'string',
  onClose,
  onConfirmedDelete,
  onRoleUpdated,
}) => {
  const { roleOptions } = useWorkspaceRoleOptions();
  const namespace = useId();
  const clientFieldId = `${namespace}-client-id`;
  const clientSecretFieldId = `${namespace}-client-secret`;
  const apiURLFieldId = `${namespace}-api-url`;
  const oauthDomainId = `${namespace}-oauth-domain`;
  const oauthScopeId = `${namespace}-oauth-scope`;
  const canUpdate = useHasWorkspacePermission(Permissions.api_client.update);
  const canDelete = useHasWorkspacePermission(Permissions.api_client.delete);
  const workspaceId = useWorkspaceId();
  const { context } = useInternalApiContext();
  const { showToast } = useToast();
  const [{ fetching: isUpdatingRole }, updateApiClient] =
    useUpdateApiClientMutation();
  const { workspace: activeWorkspaceFromContext } = useWorkspace();
  const [activeTab, setActiveTab] = useState<DetailTab>(initialTab);

  const pod = activeWorkspaceFromContext?.pod;
  const podFields = pod
    ? {
        apiUrl: pod.customerApiUrl,
        oauthUrl: `https://${pod.cognitoAuthDomain}/oauth2/token`,
        oauthScope: pod.customerApiOAuthScope,
      }
    : undefined;

  const podTextFields = podFields
    ? [
        { id: apiURLFieldId, label: 'API URL', value: podFields.apiUrl },
        { id: oauthDomainId, label: 'OAuth URL', value: podFields.oauthUrl },
        { id: oauthScopeId, label: 'OAuth Scope', value: podFields.oauthScope },
      ]
    : [];

  // Keys mirror createFragmentClient's `params` so the JSON pastes directly
  // into an SDK initialization.
  const jsonData = {
    clientId: apiClient.id,
    ...(clientSecret !== undefined ? { clientSecret } : {}),
    ...(podFields
      ? {
          apiUrl: podFields.apiUrl,
          authUrl: podFields.oauthUrl,
          scope: podFields.oauthScope,
        }
      : {}),
  };

  const cliArgs = [
    `--client-id ${apiClient.id}`,
    ...(clientSecret !== undefined ? [`--client-secret ${clientSecret}`] : []),
    ...(podFields
      ? [
          `--api-url ${podFields.apiUrl}`,
          `--oauth-url ${podFields.oauthUrl}`,
          `--oauth-scope "${podFields.oauthScope}"`,
        ]
      : []),
  ];
  const cliLoginCommand = `fragment login \\\n${cliArgs
    .map((arg) => `    ${arg}`)
    .join(' \\\n')}`;

  const handleRoleChange = useCallback(
    (nextRole: string): void => {
      const parsedRole = nextRole as WorkspaceRoleKey;
      if (parsedRole === apiClient.role || isUpdatingRole) {
        return;
      }

      const showError = () =>
        showToast(
          <Toast type="error" message="Error Updating API Client Role" />
        );

      const updateRole = async (): Promise<void> => {
        const { data, error } = await updateApiClient(
          {
            workspaceId,
            clientId: apiClient.id,
            role: parsedRole,
          },
          context
        );
        const res = getResultOrError(data?.updateApiClient, error);

        if (res.isError) {
          showError();
          return;
        }

        onRoleUpdated?.({
          ...apiClient,
          role: res.result.client.role,
        });
        showToast(<Toast type="success" message="Role Updated" />);
      };

      updateRole().catch(showError);
    },
    [
      apiClient,
      context,
      isUpdatingRole,
      onRoleUpdated,
      showToast,
      updateApiClient,
      workspaceId,
    ]
  );

  return (
    <div className="space-y-f2 max-w-full w-full">
      <div className="flex flex-col space-y-f0">
        <span className={clsx('text-main-500', apiClientFieldLabelClassName)}>
          Name
        </span>
        <span className="h-f2">{apiClient.clientName}</span>
      </div>
      <div data-testid="api-client-information-section" className="space-y-f2">
        <Tabs
          data-testid="api-client-detail-tabs"
          options={detailTabOptions}
          selectedValue={activeTab}
          onClick={setActiveTab}
          gap="gap-f1"
          height="h-f2"
          padding="px-[2px] py-f0"
        />
        {activeTab === 'string' ? (
          <div className="space-y-f2">
            <TextInputCopy
              id={clientFieldId}
              label="Client ID"
              labelClassName={apiClientFieldLabelClassName}
              value={apiClient.id}
              size="inherit"
              width="w-full"
              padding="p-0"
              border={false}
            />
            <Dropdown
              label="Role"
              labelClassName={apiClientFieldLabelClassName}
              displayValue={apiClient.role}
              onChange={handleRoleChange}
              options={
                canUpdate
                  ? roleOptions
                  : [
                      {
                        value: apiClient.role,
                        label: workspaceRoleLabels[apiClient.role],
                      },
                    ]
              }
              disabled={isUpdatingRole || !canUpdate}
              size="inherit"
              width="w-full"
              border
              data-testid="api-client-role-dropdown"
            />
            {podTextFields.map(({ id, label, value }) => (
              <TextInputCopy
                key={id}
                id={id}
                label={label}
                labelClassName={apiClientFieldLabelClassName}
                value={value}
                size="inherit"
                width="w-full"
                padding="p-0"
                border={false}
              />
            ))}
            {clientSecret !== undefined ? (
              <TextInputCopy
                id={clientSecretFieldId}
                label="clientSecret"
                labelClassName={apiClientFieldLabelClassName}
                size="inherit"
                width="w-full"
                padding="p-0"
                border={false}
                value={clientSecret}
              />
            ) : null}
          </div>
        ) : null}
        {activeTab === 'json' ? (
          <CodeBlock language="json" truncateLongLines>
            {JSON.stringify(jsonData, null, 2)}
          </CodeBlock>
        ) : null}
        {activeTab === 'cli' ? (
          <CodeBlock language="bash" truncateLongLines>
            {cliLoginCommand}
          </CodeBlock>
        ) : null}
      </div>
      {clientSecret !== undefined ? (
        <div className="space-y-f2">
          <ErrorText>Note: clientSecret will not be shown again.</ErrorText>
          <PrimaryButtonV2 onClick={onClose}>Close</PrimaryButtonV2>
        </div>
      ) : null}
      {clientSecret === undefined && canDelete ? (
        <DeleteClientForm apiClient={apiClient} onDelete={onConfirmedDelete} />
      ) : null}
    </div>
  );
};
